import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CurrentUserInformation from '../../../molecules/CurrentUserInformation/CurrentUserInformation';

import { Namespace } from '../../../../translation/namespace';
import { ISonURL } from '../../../../types/IUrl';

type MemberDescriptionProps = {
  roles: string[];
  membershipType: string;
  profileLink: ISonURL;
  profileLinkActivationRequester?: ISonURL | null;
};

const extractLanguageKey = (membershipType: string) => {
  if (
    [
      'activationApprovalPending',
      'activationRequestPending',
      'canApproveActivation',
      'canRequestActivation',
      'invited',
      'member',
      'notActiveYet',
      'requestPending',
    ].includes(membershipType)
  ) {
    return membershipType;
  }
  return 'noMember';
};

/**
 * This Method describe different cases for teamroom
 * Case1: You are no teamroom member, message "you are no teamroom member".
 * Case2: You are a teamroom member, message "you are a teamroom member with the role of
 * e.g. "teamroom coordinator".
 * Case3: You are no teamroom member but a role was assigned to you, message "you are a teamroom member with
 * the role of e.g. "teamroom coordinator" (not yet adopted)
 * Case4: You are invited to a teamroom (role description not necessary until now)
 */
export default function MemberDescription({
  roles,
  membershipType,
  profileLink,
  profileLinkActivationRequester,
}: MemberDescriptionProps) {
  const { t, i18n } = useTranslation(Namespace.TeamroomManagement);

  const createText = (languageKey: string) => t(`infoTexts.${languageKey}`);
  const createTextWithRoles = (languageKey: string) => {
    const components = [
      <strong key="key1" className="global--bold">
        {roles.join(', ')}
      </strong>,
    ];
    if (membershipType === 'canRequestActivation') {
      components.push(
        <strong key="key2" className="global--bold">
          {t('glossary:coordinator')}
        </strong>
      );
    }
    return (
      <Trans
        i18nKey={`infoTexts.${languageKey}`}
        i18n={i18n}
        t={t}
        count={roles.length}
        // required in addtion to the count attribute for the plural feature
        options={{ count: roles.length }}
        components={components}
      />
    );
  };

  const createMemberDescription = () => {
    const languageKey = extractLanguageKey(membershipType);
    switch (languageKey) {
      case 'noMember':
      case 'invited':
      case 'canApproveActivation':
      case 'activationRequestPending':
        return createText(languageKey);
      case 'member':
      case 'requestPending':
      case 'canRequestActivation':
      case 'notActiveYet':
      case 'activationApprovalPending':
        return createTextWithRoles(languageKey);
      default:
        return null;
    }
  };
  return (
    <CurrentUserInformation
      profileLink={profileLinkActivationRequester || profileLink}
      withComma={!profileLinkActivationRequester}
    >
      {createMemberDescription()}
    </CurrentUserInformation>
  );
}
